import type { RankOrderQuestion } from '../../fetchSurvey/types/rankOrder';
import type { RankOrderAnswers } from '../types';
import {
  getOrderedOptionIdsFromRanks,
  normalizeRankOrderAnswers,
} from '../../surveyLogic/fields/rankOrderUtils';

/** API option-level key: `{questionId}-:-{optionId}` */
export function buildRankOrderOptionLevelKey(questionId: string, optionId: string): string {
  return `${questionId}-:-${optionId}`;
}

export type RankOrderSubmitResult = {
  /** Ordered option ids from rank 1 → N for questionToAnswers[questionId]. */
  orderedOptionIds: string[];
  /** Per-option rank strings for optionLevelAnswers. */
  optionLevelAnswers: Record<string, string[]>;
};

export function resolveRankOrderSubmitValues(
  question: RankOrderQuestion,
  value: unknown
): RankOrderSubmitResult | null {
  const rankAnswers = normalizeRankOrderAnswers(value);
  const optionIds = question.options.map(option => option.id);
  const orderedOptionIds = getOrderedOptionIdsFromRanks(rankAnswers, optionIds);

  if (orderedOptionIds.length === 0) {
    return null;
  }

  const optionLevelAnswers: Record<string, string[]> = {};

  for (const optionId of orderedOptionIds) {
    const rank = rankAnswers[optionId];
    if (rank === undefined) {
      continue;
    }

    optionLevelAnswers[buildRankOrderOptionLevelKey(question.id, optionId)] = [String(rank)];
  }

  return {
    orderedOptionIds,
    optionLevelAnswers,
  };
}

export function isRankOrderQuestionAnswerComplete(
  question: RankOrderQuestion,
  value: unknown,
  visibleOptionIds: string[]
): boolean {
  if (visibleOptionIds.length === 0) {
    return true;
  }

  const rankAnswers = normalizeRankOrderAnswers(value) as RankOrderAnswers;
  const rankedVisibleCount = visibleOptionIds.filter(
    optionId => rankAnswers[optionId] !== undefined
  ).length;

  if (question.requireRankAll || question.required) {
    if (rankedVisibleCount < visibleOptionIds.length) {
      return false;
    }

    const usedRanks = new Set<number>();
    for (const optionId of visibleOptionIds) {
      const rank = rankAnswers[optionId];
      if (rank === undefined || usedRanks.has(rank)) {
        return false;
      }
      usedRanks.add(rank);
    }

    return true;
  }

  return !question.required || rankedVisibleCount > 0;
}
